import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";

import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import GroupIcon from "@material-ui/icons/Group";
import StopIcon from "@material-ui/icons/HighlightOff";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AssignmentIcon from "@material-ui/icons/Assignment";
import RepeatIcon from "@material-ui/icons/Repeat";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import StatusChip from "../../components/StatusChip";
import ConfirmationModal from "../../components/ConfirmationModal";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import { displayClientName } from "../../utils/clientName";

const ENROLL_STATUS = {
  running: { label: "Em andamento", tone: "info" },
  replied: { label: "Respondeu", tone: "success" },
  completed: { label: "Concluída", tone: "neutral" },
  stopped: { label: "Interrompida", tone: "warning" },
};

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  cardHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  name: { fontWeight: 600 },
  meta: { color: theme.palette.text.secondary, fontSize: 12 },
  // A trilha de passos e o coracao da tela: o vendedor precisa entender o
  // roteiro inteiro de relance, sem abrir nada.
  steps: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    overflowX: "auto",
    paddingBottom: 4,
  },
  step: {
    flex: "0 0 auto",
    minWidth: 190,
    maxWidth: 240,
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(148,163,184,0.06)" : theme.palette.grey[50],
  },
  stepHead: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  stepBody: {
    fontSize: 12,
    lineHeight: 1.45,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  itemRow: { display: "flex", gap: theme.spacing(1), alignItems: "flex-start", marginBottom: theme.spacing(1) },
  enrollRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const EMPTY_STEP = { delayDays: 2, channel: "whatsapp", body: "", taskTitle: "" };

const Cadences = () => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // {id?, form}
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [enrollOf, setEnrollOf] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [enrollmentsOf, setEnrollmentsOf] = useState(null);
  const [enrollments, setEnrollments] = useState([]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cadences");
      setRows(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useSocketSubscription("cadence", fetchRows);

  const openNew = () =>
    setModal({
      id: null,
      form: {
        name: "",
        description: "",
        stopOnReply: true,
        businessHoursOnly: true,
        steps: [{ ...EMPTY_STEP, delayDays: 0 }],
      },
    });

  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/cadences/${row.id}`);
      setModal({
        id: data.id,
        form: {
          name: data.name,
          description: data.description || "",
          stopOnReply: data.stopOnReply,
          businessHoursOnly: data.businessHoursOnly,
          steps: (data.steps || []).map((s) => ({
            delayDays: s.delayDays,
            channel: s.channel,
            body: s.body || "",
            taskTitle: s.taskTitle || "",
          })),
        },
      });
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...modal.form,
        steps: modal.form.steps.filter((s) =>
          s.channel === "task" ? s.taskTitle.trim() : s.body.trim()
        ),
      };
      if (modal.id) await api.put(`/cadences/${modal.id}`, payload);
      else await api.post("/cadences", payload);
      toast.success(modal.id ? "Cadência atualizada" : "Cadência criada");
      setModal(null);
      fetchRows();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    try {
      await api.put(`/cadences/${row.id}`, { active: !row.active });
      fetchRows();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/cadences/${deleting.id}`);
      toast.success("Cadência removida");
      fetchRows();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  const openEnroll = async (row) => {
    setEnrollOf(row);
    setClientId("");
    if (!clients.length) {
      api.get("/clients", { params: { pageSize: 200 } })
        .then((r) => setClients(r.data.clients || []))
        .catch(() => setClients([]));
    }
  };

  const handleEnroll = async () => {
    try {
      await api.post(`/cadences/${enrollOf.id}/enroll`, { clientId: Number(clientId) });
      toast.success("Cliente inscrito. O primeiro toque sai no próximo ciclo do worker.");
      setEnrollOf(null);
      fetchRows();
    } catch (err) {
      toastError(err);
    }
  };

  const openEnrollments = async (row) => {
    setEnrollmentsOf(row);
    try {
      const { data } = await api.get("/cadences/enrollments", { params: { cadenceId: row.id } });
      setEnrollments(data || []);
    } catch (err) {
      toastError(err);
      setEnrollments([]);
    }
  };

  const stopEnrollment = async (id) => {
    try {
      await api.post(`/cadences/enrollments/${id}/stop`, {});
      toast.success("Inscrição interrompida");
      openEnrollments(enrollmentsOf);
      fetchRows();
    } catch (err) {
      toastError(err);
    }
  };

  const setStep = (idx, field) => (e) =>
    setModal((m) => ({
      ...m,
      form: {
        ...m.form,
        steps: m.form.steps.map((s, i) => (i === idx ? { ...s, [field]: e.target.value } : s)),
      },
    }));

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `Remover a cadência "${deleting.name}"?` : ""}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        As inscrições em andamento são encerradas — nenhum cliente fica esperando
        um passo que deixou de existir.
      </ConfirmationModal>

      <PageHeader
        title="Cadências de prospecção"
        count={rows.length}
        subtitle="Sequências de toques com intervalo definido. Param sozinhas quando o cliente responde."
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
            Nova cadência
          </Button>
        }
      />

      <div style={{ paddingTop: 16 }}>
        {!loading && !rows.length && (
          <Paper variant="outlined">
            <div className={classes.empty}>
              <RepeatIcon style={{ fontSize: 40, opacity: 0.4 }} />
              <Typography variant="subtitle1" color="textPrimary">
                Nenhuma cadência criada
              </Typography>
              <Typography variant="body2">
                Monte um roteiro de toques e inscreva os leads que não responderam.
              </Typography>
              <Button
                style={{ marginTop: 12 }}
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openNew}
              >
                Nova cadência
              </Button>
            </div>
          </Paper>
        )}

        {rows.map((c) => (
          <Paper variant="outlined" key={c.id} className={classes.card}>
            <div className={classes.cardHead}>
              <div>
                <div className={classes.name}>{c.name}</div>
                <div className={classes.meta}>
                  {(c.steps || []).length} passos
                  {c.runningEnrollments ? ` · ${c.runningEnrollments} em andamento` : " · ninguém inscrito"}
                  {c.stopOnReply ? " · para na resposta" : " · não para na resposta"}
                  {c.businessHoursOnly ? " · horário comercial" : ""}
                </div>
                {c.description && (
                  <Typography variant="caption" className={classes.meta}>
                    {c.description}
                  </Typography>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Tooltip title={c.active ? "Desativar" : "Ativar"}>
                  <Switch size="small" checked={!!c.active} onChange={() => handleToggle(c)} />
                </Tooltip>
                <Tooltip title="Inscrever cliente">
                  <span>
                    <IconButton size="small" disabled={!c.active} onClick={() => openEnroll(c)}>
                      <PersonAddIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Ver inscrições">
                  <IconButton size="small" onClick={() => openEnrollments(c)}>
                    <GroupIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => openEdit(c)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remover">
                  <IconButton size="small" onClick={() => setDeleting(c)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            <div className={classes.steps}>
              {(c.steps || []).map((s, i) => (
                <div key={s.id} className={classes.step}>
                  <div className={classes.stepHead}>
                    {s.channel === "task" ? (
                      <AssignmentIcon style={{ fontSize: 14 }} />
                    ) : (
                      <WhatsAppIcon style={{ fontSize: 14 }} />
                    )}
                    {i === 0 ? "Início" : `+${s.delayDays}d`}
                  </div>
                  <div className={classes.stepBody}>
                    {s.channel === "task" ? s.taskTitle : s.body}
                  </div>
                </div>
              ))}
            </div>
          </Paper>
        ))}
      </div>

      {/* Criação / edição */}
      <Dialog open={!!modal} onClose={() => setModal(null)} maxWidth="md" fullWidth>
        <DialogTitle>{modal?.id ? "Editar cadência" : "Nova cadência"}</DialogTitle>
        <DialogContent>
          {modal && (
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Nome *"
                  value={modal.form.name}
                  onChange={(e) => setModal({ ...modal, form: { ...modal.form, name: e.target.value } })}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  label="Descrição"
                  value={modal.form.description}
                  onChange={(e) => setModal({ ...modal, form: { ...modal.form, description: e.target.value } })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={modal.form.stopOnReply}
                      onChange={(e) => setModal({ ...modal, form: { ...modal.form, stopOnReply: e.target.checked } })}
                    />
                  }
                  label="Encerrar quando o cliente responder"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={modal.form.businessHoursOnly}
                      onChange={(e) => setModal({ ...modal, form: { ...modal.form, businessHoursOnly: e.target.checked } })}
                    />
                  }
                  label="Só em horário comercial (9h–18h, dias úteis)"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Passos
                </Typography>
                {modal.form.steps.map((s, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={idx} className={classes.itemRow}>
                    <TextField
                      style={{ width: 130 }}
                      type="number"
                      label={idx === 0 ? "Dias (início)" : "Dias após o anterior"}
                      value={s.delayDays}
                      onChange={setStep(idx, "delayDays")}
                    />
                    <TextField
                      select
                      style={{ width: 150 }}
                      label="Tipo"
                      value={s.channel}
                      onChange={setStep(idx, "channel")}
                    >
                      <MenuItem value="whatsapp">Mensagem</MenuItem>
                      <MenuItem value="task">Tarefa</MenuItem>
                    </TextField>
                    {s.channel === "task" ? (
                      <TextField
                        style={{ flex: 1 }}
                        label="Título da tarefa"
                        value={s.taskTitle}
                        onChange={setStep(idx, "taskTitle")}
                      />
                    ) : (
                      <TextField
                        style={{ flex: 1 }}
                        multiline
                        label="Mensagem"
                        value={s.body}
                        onChange={setStep(idx, "body")}
                      />
                    )}
                    <IconButton
                      size="small"
                      disabled={modal.form.steps.length === 1}
                      onClick={() =>
                        setModal({
                          ...modal,
                          form: { ...modal.form, steps: modal.form.steps.filter((_, i) => i !== idx) },
                        })
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setModal({ ...modal, form: { ...modal.form, steps: [...modal.form.steps, { ...EMPTY_STEP }] } })
                  }
                >
                  Adicionar passo
                </Button>
                <Typography variant="caption" style={{ display: "block", marginTop: 8, opacity: 0.75 }}>
                  Variáveis disponíveis: {"{{primeiro_nome}}"}, {"{{nome}}"}, {"{{empresa}}"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModal(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={saving || !modal?.form.name.trim()}
          >
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inscrever */}
      <Dialog open={!!enrollOf} onClose={() => setEnrollOf(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Inscrever em “{enrollOf?.name}”</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Cliente"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            style={{ marginTop: 8 }}
          >
            <MenuItem value="">—</MenuItem>
            {clients.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {displayClientName(c)}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12 }}>
            O cliente precisa ter um atendimento aberto para os passos de mensagem.
            A cadência encerra sozinha se ele responder.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollOf(null)}>Cancelar</Button>
          <Button color="primary" variant="contained" disabled={!clientId} onClick={handleEnroll}>
            Inscrever
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inscrições */}
      <Dialog open={!!enrollmentsOf} onClose={() => setEnrollmentsOf(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Inscrições — {enrollmentsOf?.name}</DialogTitle>
        <DialogContent>
          {!enrollments.length && (
            <Typography variant="body2" color="textSecondary">
              Ninguém inscrito nesta cadência.
            </Typography>
          )}
          {enrollments.map((e) => {
            const meta = ENROLL_STATUS[e.status] || { label: e.status, tone: "neutral" };
            return (
              <div key={e.id} className={classes.enrollRow}>
                <div>
                  <Typography variant="subtitle2">{displayClientName(e.client)}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    passo {e.currentStep + 1}
                    {e.nextRunAt
                      ? ` · próximo em ${new Date(e.nextRunAt).toLocaleDateString("pt-BR")}`
                      : ""}
                    {e.stopReason ? ` · ${e.stopReason}` : ""}
                  </Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StatusChip dot tone={meta.tone} label={meta.label} />
                  {e.status === "running" && (
                    <Tooltip title="Interromper">
                      <IconButton size="small" onClick={() => stopEnrollment(e.id)}>
                        <StopIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollmentsOf(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Cadences;
